import React, { useState } from "react";
import { Video, Sparkles, Download, FileText, Play, Image as ImageIcon, Eye, Check } from "lucide-react";

import { Media } from "@/services/bedrock";
import { MediaPlayer } from "@/components/MediaPlayer";
import { Button, Dialog } from "@/components/ui";
import { VideoTranscriptsModal } from "./VideoTranscriptsModal";
import { cn } from "@/lib/utils";

import "./styles.scss";

interface Props {
  videoProjectId: number;
  type: "EDITED" | "ORIGINAL";
  videos: Media[];
  isEditor?: boolean;
  variant?: "square" | "original";
  cols?: number;
  gridClassName?: string;
}

const gridColsClasses: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

export const VideosSection: React.FC<Props> = ({
  videoProjectId,
  type,
  videos,
  isEditor = false,
  variant = "square",
  cols = 3,
  gridClassName,
}) => {
  const [showTranscripts, setShowTranscripts] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<Media | null>(null);
  const [selectedVideoForPlayer, setSelectedVideoForPlayer] = useState<Media | null>(null);
  const [showPlayerModal, setShowPlayerModal] = useState(false);

  const [downloadProgress, setDownloadProgress] = useState<{
    [uri: string]: { percentage?: number; mb?: string };
  }>({});

  const [downloadedUris, setDownloadedUris] = useState<{ [uri: string]: boolean }>({});

  const downloadFile = async (filePath: string, fileName: string) => {
    setDownloadProgress(prev => ({ ...prev, [filePath]: { percentage: 0 } }));

    try {
      const response = await fetch(filePath);
      if (!response.ok) throw new Error("Network response was not ok");

      const contentLength = response.headers.get("content-length");
      const total = contentLength ? parseInt(contentLength, 10) : 0;

      const reader = response.body ? response.body.getReader() : null;
      if (!reader) throw new Error("ReadableStream not supported or null");

      let receivedLength = 0;
      const chunks: Uint8Array[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        if (value) {
          chunks.push(value);
          receivedLength += value.length;

          if (total > 0) {
            const percentage = Math.round((receivedLength / total) * 100);
            setDownloadProgress(prev => ({ ...prev, [filePath]: { percentage } }));
          } else {
            const mb = (receivedLength / (1024 * 1024)).toFixed(1);
            setDownloadProgress(prev => ({ ...prev, [filePath]: { mb } }));
          }
        }
      }

      const chunksAll = new Uint8Array(receivedLength);
      let position = 0;
      for (const chunk of chunks) {
        chunksAll.set(chunk, position);
        position += chunk.length;
      }

      const blob = new Blob([chunksAll]);
      const urlObject = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = urlObject;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloadProgress(prev => ({ ...prev, [filePath]: { percentage: 100 } }));
      setDownloadedUris(prev => ({ ...prev, [filePath]: true }));

      setTimeout(() => {
        window.URL.revokeObjectURL(urlObject);
        setDownloadProgress(prev => {
          const next = { ...prev };
          delete next[filePath];
          return next;
        });
      }, 1500);
    } catch (error) {
      console.warn("CORS or network issue during blob download, falling back to direct download tab:", error);

      const link = document.createElement("a");
      link.href = filePath;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloadedUris(prev => ({ ...prev, [filePath]: true }));

      setDownloadProgress(prev => {
        const next = { ...prev };
        delete next[filePath];
        return next;
      });
    }
  };

  return (
    <div className="filesContainer flex flex-col gap-3">
      {videos.length > 0 ? (
        <div className={cn("grid gap-5", gridColsClasses[cols] || "grid-cols-3", gridClassName)}>
          {videos.map((media, index) => {
            const hasTranscripts = media.transcripts && media.transcripts.length > 0;
            const progress = downloadProgress[media.uri];
            const isDownloading = progress !== undefined;
            const isDownloaded = !!downloadedUris[media.uri];
            const progressLabel = isDownloading
              ? progress.percentage !== undefined
                ? `Downloading... ${progress.percentage}%`
                : `Downloading... ${progress.mb} MB`
              : "Download File";

            return (
              <div
                key={`media_${type}_container_${index}`}
                className="flex flex-col bg-white border border-slate-200/80 p-4 rounded-xl shadow-xs hover:border-slate-300/80 hover:shadow-sm transition-all"
              >
                <div
                  className={cn(
                    "relative w-full rounded-lg overflow-hidden bg-slate-900 border border-slate-100 flex items-center justify-center cursor-pointer group",
                    variant === "square" ? "thumbnailWrapper aspect-square" : "thumbnailWrapperOriginal"
                  )}
                  onClick={() => {
                    setSelectedVideoForPlayer(media);
                    setShowPlayerModal(true);
                  }}
                >
                  <MediaPlayer media={media} previewOnly={true} />

                  {media.category === "VIDEO" ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                      <div className="flex items-center justify-center rounded-full bg-white/90 group-hover:bg-white group-hover:scale-110 text-slate-900 size-12 shadow-md transition-all">
                        <Play className="size-6 fill-slate-900 ml-0.5" />
                      </div>
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                      <div className="flex items-center justify-center rounded-full bg-white/0 group-hover:bg-white group-hover:scale-110 text-slate-900 size-12 opacity-0 group-hover:opacity-100 shadow-md transition-all">
                        <Eye className="size-6 text-slate-900" />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2 mt-3.5">
                  <Button
                    variant={isDownloaded && !isDownloading ? "outline" : "default"}
                    size="sm"
                    className={cn(
                      "w-full flex items-center justify-center gap-1.5 font-semibold text-xs shadow-2xs hover:shadow-xs transition-all",
                      isDownloaded &&
                        !isDownloading &&
                        "text-emerald-700 border-emerald-200 bg-emerald-50/10 hover:bg-emerald-50/20 hover:text-emerald-800 hover:border-emerald-300"
                    )}
                    disabled={isDownloading}
                    onClick={() =>
                      downloadFile(media.uri, `${videoProjectId}_${type}_${media.category}_${index}.${media.extension}`)
                    }
                  >
                    {isDownloading ? (
                      <>
                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent shrink-0" />
                        <span>{progressLabel}</span>
                      </>
                    ) : isDownloaded ? (
                      <>
                        <Check className="size-3.5 text-emerald-600 animate-bounce" />
                        <span>Downloaded</span>
                      </>
                    ) : (
                      <>
                        <Download className="size-3.5" />
                        <span>Download File</span>
                      </>
                    )}
                  </Button>

                  {isEditor && hasTranscripts && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full flex items-center justify-center gap-1.5 font-semibold text-xs text-slate-600 border-slate-200 hover:bg-slate-50/50 hover:text-slate-800 transition-colors"
                      onClick={() => {
                        setSelectedVideo(media);
                        setShowTranscripts(true);
                      }}
                    >
                      <FileText className="size-3.5" />
                      View Transcript
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center p-6 bg-slate-50 border border-slate-100 rounded-xl text-slate-400 text-xs font-medium">
          No files available in this section
        </div>
      )}

      {showTranscripts && selectedVideo && (
        <VideoTranscriptsModal
          open={showTranscripts}
          videos={[selectedVideo]}
          onClose={() => {
            setShowTranscripts(false);
            setSelectedVideo(null);
          }}
        />
      )}

      {showPlayerModal && selectedVideoForPlayer && (
        <Dialog
          open={showPlayerModal}
          onOpenChange={isOpen => {
            if (!isOpen) {
              setShowPlayerModal(false);
              setSelectedVideoForPlayer(null);
            }
          }}
          size="lg"
          showCloseButton={true}
          className="mediaPreviewDialog max-h-[calc(100vh-4rem)] flex flex-col overflow-hidden"
        >
          <Dialog.Header>
            <Dialog.Title className="flex items-center gap-1.5 text-slate-800 text-base font-bold">
              {selectedVideoForPlayer.category === "VIDEO" ? (
                type === "EDITED" ? (
                  <Sparkles className="size-5 text-indigo-500" />
                ) : (
                  <Video className="size-5 text-slate-500" />
                )
              ) : (
                <ImageIcon className="size-5 text-indigo-500" />
              )}
              <span>{selectedVideoForPlayer.category === "VIDEO" ? "Watch Video" : "View Image"}</span>
            </Dialog.Title>
          </Dialog.Header>

          <Dialog.Content className="p-0 bg-black flex-1 flex items-center justify-center overflow-hidden rounded-b-xl">
            {selectedVideoForPlayer.category === "VIDEO" ? (
              <div className="w-full h-full flex items-center justify-center bg-black overflow-hidden">
                <MediaPlayer media={selectedVideoForPlayer} autoPlay={true} />
              </div>
            ) : (
              <div className="w-full h-full p-4 flex items-center justify-center bg-slate-950 overflow-hidden">
                <img
                  src={selectedVideoForPlayer.uri || selectedVideoForPlayer.thumbnailUri}
                  alt={selectedVideoForPlayer.category}
                  className="rounded-lg shadow-lg"
                />
              </div>
            )}
          </Dialog.Content>
        </Dialog>
      )}
    </div>
  );
};
